import { applyPatches, enablePatches, produceWithPatches, type Patch } from "immer";
import { nanoid } from "nanoid";
import { create } from "zustand";
import { descendantsOf } from "../model/graph";
import {
  createPrimitiveNode,
  makeEmptyDocument,
  touchDocument,
  type EvaluationResult,
  type FeatureNode,
  type MotiveDocument,
  type NodeDiagnostic,
  type NodeType
} from "../model/types";

enablePatches();

type HistoryEntry = { label: string; patches: Patch[]; inversePatches: Patch[] };
export type AppMode = "design" | "motive";
export type SaveStatus = "saved" | "saving" | "unsaved" | "error";
export type KernelStatus = "loading" | "ready" | "rebuilding" | "error";
export type TransformMode = "select" | "translate" | "rotate" | "scale";

type StoreState = {
  document: MotiveDocument;
  revision: number;
  selectedNodeIds: string[];
  mode: AppMode;
  historyPast: HistoryEntry[];
  historyFuture: HistoryEntry[];
  evaluation: EvaluationResult | null;
  diagnostics: NodeDiagnostic[];
  saveStatus: SaveStatus;
  kernelStatus: KernelStatus;
  statusMessage: string;
  commandPaletteOpen: boolean;
  transformMode: TransformMode;
  newProjectCounter: number;

  setDocument: (document: MotiveDocument) => void;
  newDocument: (name?: string) => void;
  renameDocument: (name: string) => void;
  selectNode: (id: string, additive?: boolean) => void;
  clearSelection: () => void;
  setMode: (mode: AppMode) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  setTransformMode: (mode: TransformMode) => void;
  addPrimitive: (type: Extract<NodeType, "box" | "cylinder" | "sphere" | "cone" | "wedge">) => void;
  addOperation: (type: Extract<NodeType, "transform" | "hole" | "fillet" | "chamfer" | "mirror" | "linearPattern" | "circularPattern">) => void;
  addBoolean: (operation: "union" | "subtract" | "intersect") => void;
  updateNode: (nodeId: string, updater: (node: FeatureNode) => void, label?: string) => void;
  updateParameterValue: (parameterId: string, value: string) => void;
  updateParameter: (parameterId: string, updates: Record<string, string | number | undefined>) => void;
  updateViewport: (updates: Partial<MotiveDocument["viewport"]>) => void;
  addParameter: () => void;
  removeParameter: (parameterId: string) => void;
  exposeParameter: (parameterId: string, exposed: boolean) => void;
  updateExposedParameter: (parameterId: string, updates: Record<string, string | number | undefined>) => void;
  deleteSelected: () => void;
  duplicateSelected: () => void;
  toggleSelectedEnabled: () => void;
  toggleNodeVisibility: (nodeId: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  setEvaluation: (evaluation: EvaluationResult) => void;
  setKernelStatus: (status: KernelStatus, message?: string) => void;
  setSaveStatus: (status: SaveStatus, message?: string) => void;
};

const MAX_HISTORY = 120;
const colors = ["#73d8b4", "#64b5f6", "#f5b76f", "#d99cf3", "#ef8892", "#8ad5e6"];

function operationBase(name: string, inputs: string[], color = "#73d8b4") {
  return { id: nanoid(), name, enabled: true, visible: true, color, inputNodeIds: inputs };
}

export const useModelStore = create<StoreState>((set, get) => {
  const commit = (label: string, recipe: (draft: MotiveDocument) => void, selectedAfter?: string[]): void => {
    const current = get().document;
    const [next, patches, inversePatches] = produceWithPatches(current, (draft) => {
      recipe(draft);
      touchDocument(draft);
    });
    if (patches.length === 0) return;
    const entry: HistoryEntry = { label, patches, inversePatches };
    set((state) => ({
      document: next,
      revision: state.revision + 1,
      historyPast: [...state.historyPast, entry].slice(-MAX_HISTORY),
      historyFuture: [],
      selectedNodeIds: selectedAfter ?? state.selectedNodeIds,
      saveStatus: "unsaved",
      statusMessage: label
    }));
  };

  return {
    document: makeEmptyDocument(),
    revision: 0,
    selectedNodeIds: [],
    mode: "design",
    historyPast: [],
    historyFuture: [],
    evaluation: null,
    diagnostics: [],
    saveStatus: "saved",
    kernelStatus: "loading",
    statusMessage: "Loading CAD kernel…",
    commandPaletteOpen: false,
    transformMode: "select",
    newProjectCounter: 1,

    setDocument(document) {
      set((state) => ({
        document: structuredClone(document),
        revision: state.revision + 1,
        selectedNodeIds: [],
        historyPast: [],
        historyFuture: [],
        evaluation: null,
        diagnostics: [],
        saveStatus: "saved",
        statusMessage: `Opened ${document.name}`,
        mode: "design"
      }));
    },

    newDocument(name) {
      const counter = get().newProjectCounter;
      const document = makeEmptyDocument(name ?? `Untitled model ${counter}`);
      set((state) => ({
        document,
        revision: state.revision + 1,
        selectedNodeIds: [],
        historyPast: [],
        historyFuture: [],
        evaluation: null,
        diagnostics: [],
        saveStatus: "unsaved",
        statusMessage: "New model",
        mode: "design",
        newProjectCounter: counter + 1
      }));
    },

    renameDocument(name) {
      const trimmed = name.trim();
      if (!trimmed || trimmed === get().document.name) return;
      commit("Rename model", (draft) => {
        draft.name = trimmed;
      });
    },

    selectNode(id, additive = false) {
      set((state) => {
        if (!additive) return { selectedNodeIds: [id] };
        const exists = state.selectedNodeIds.includes(id);
        return { selectedNodeIds: exists ? state.selectedNodeIds.filter((item) => item !== id) : [...state.selectedNodeIds, id] };
      });
    },

    clearSelection() {
      set({ selectedNodeIds: [] });
    },

    setMode(mode) {
      set({ mode });
    },

    setCommandPaletteOpen(open) {
      set({ commandPaletteOpen: open });
    },

    setTransformMode(transformMode) {
      set({ transformMode });
    },

    addPrimitive(type) {
      const offset = get().document.nodes.filter((node) => node.inputNodeIds.length === 0).length * 18;
      const node = createPrimitiveNode(type, offset);
      node.color = colors[get().document.nodes.length % colors.length] ?? "#73d8b4";
      commit(`Add ${node.name}`, (draft) => {
        draft.nodes.push(node);
      }, [node.id]);
    },

    addOperation(type) {
      const selected = get().selectedNodeIds;
      if (selected.length !== 1) {
        set({ statusMessage: "Select exactly one feature first" });
        return;
      }
      const input = selected[0];
      if (!input) return;
      const color = get().document.nodes.find((node) => node.id === input)?.color ?? "#73d8b4";
      let node: FeatureNode;
      switch (type) {
        case "transform":
          node = { ...operationBase("Transform", [input], color), type, translation: ["10", "0", "0"], rotation: ["0", "0", "0"], scale: "1" };
          break;
        case "hole":
          node = { ...operationBase("Hole", [input], color), type, radius: "4", depth: "100", position: ["0", "0", "-10"], axis: "Z" };
          break;
        case "fillet":
          node = { ...operationBase("Fillet", [input], color), type, radius: "2", selection: "vertical" };
          break;
        case "chamfer":
          node = { ...operationBase("Chamfer", [input], color), type, distance: "2", selection: "vertical" };
          break;
        case "mirror":
          node = { ...operationBase("Mirror", [input], color), type, plane: "YZ", keepOriginal: true, fuseCopies: false };
          break;
        case "linearPattern":
          node = { ...operationBase("Linear pattern", [input], color), type, count: "3", spacing: "30", direction: "X", fuseCopies: false };
          break;
        case "circularPattern":
          node = {
            ...operationBase("Circular pattern", [input], color),
            type,
            count: "4",
            totalAngle: "360",
            axis: "Z",
            origin: ["0", "0", "0"],
            fuseCopies: false
          };
          break;
      }
      commit(`Add ${node.name}`, (draft) => {
        draft.nodes.push(node);
      }, [node.id]);
    },

    addBoolean(operation) {
      const selected = get().selectedNodeIds;
      if (selected.length !== 2) {
        set({ statusMessage: "Select exactly two features; the second is the tool" });
        return;
      }
      const [left, right] = selected;
      if (!left || !right) return;
      const label = operation === "union" ? "Union" : operation === "subtract" ? "Subtract" : "Intersect";
      const color = get().document.nodes.find((node) => node.id === left)?.color ?? "#73d8b4";
      const node: FeatureNode = { ...operationBase(label, [left, right], color), type: "boolean", operation };
      commit(`Add ${label}`, (draft) => {
        draft.nodes.push(node);
      }, [node.id]);
    },

    updateNode(nodeId, updater, label = "Edit feature") {
      commit(label, (draft) => {
        const node = draft.nodes.find((candidate) => candidate.id === nodeId);
        if (node) updater(node);
      });
    },

    updateParameterValue(parameterId, value) {
      commit("Edit parameter", (draft) => {
        const parameter = draft.parameters[parameterId];
        if (parameter) parameter.value = value;
      });
    },

    updateParameter(parameterId, updates) {
      commit("Edit parameter", (draft) => {
        const parameter = draft.parameters[parameterId];
        if (!parameter) return;
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined) delete (parameter as unknown as Record<string, unknown>)[key];
          else (parameter as unknown as Record<string, unknown>)[key] = value;
        }
      });
    },

    updateViewport(updates) {
      commit("Update viewport", (draft) => {
        Object.assign(draft.viewport, updates);
      });
    },

    addParameter() {
      const existingNames = new Set(Object.values(get().document.parameters).map((parameter) => parameter.name));
      let index = 1;
      while (existingNames.has(`dimension${index}`)) index += 1;
      const id = nanoid();
      commit("Add parameter", (draft) => {
        draft.parameters[id] = {
          id,
          name: `dimension${index}`,
          label: `Dimension ${index}`,
          type: "length",
          value: "10",
          minimum: 0,
          step: 1
        };
      });
    },

    removeParameter(parameterId) {
      commit("Remove parameter", (draft) => {
        delete draft.parameters[parameterId];
        draft.exposedParameters = draft.exposedParameters.filter((entry) => entry.parameterId !== parameterId);
      });
    },

    exposeParameter(parameterId, exposed) {
      commit(exposed ? "Expose parameter" : "Hide parameter", (draft) => {
        const existing = draft.exposedParameters.find((entry) => entry.parameterId === parameterId);
        const parameter = draft.parameters[parameterId];
        if (exposed && !existing && parameter) {
          draft.exposedParameters.push({
            parameterId,
            label: parameter.label,
            group: "Dimensions",
            minimum: parameter.minimum,
            maximum: parameter.maximum,
            step: parameter.step ?? 1
          });
        } else if (!exposed) {
          draft.exposedParameters = draft.exposedParameters.filter((entry) => entry.parameterId !== parameterId);
        }
      });
    },

    updateExposedParameter(parameterId, updates) {
      commit("Edit exposed parameter", (draft) => {
        const exposed = draft.exposedParameters.find((entry) => entry.parameterId === parameterId);
        if (!exposed) return;
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined) delete (exposed as Record<string, unknown>)[key];
          else (exposed as Record<string, unknown>)[key] = value;
        }
      });
    },

    deleteSelected() {
      const selected = get().selectedNodeIds;
      if (selected.length === 0) return;
      const remove = new Set<string>(selected);
      for (const id of selected) for (const descendant of descendantsOf(get().document.nodes, id)) remove.add(descendant);
      commit(`Delete ${remove.size} feature${remove.size === 1 ? "" : "s"}`, (draft) => {
        draft.nodes = draft.nodes.filter((node) => !remove.has(node.id));
      }, []);
    },

    duplicateSelected() {
      const selectedId = get().selectedNodeIds[0];
      if (!selectedId) return;
      const source = get().document.nodes.find((node) => node.id === selectedId);
      if (!source) return;
      const clone = structuredClone(source);
      clone.id = nanoid();
      clone.name = `${source.name} copy`;
      clone.color = colors[get().document.nodes.length % colors.length] ?? source.color;
      if ("placement" in clone) clone.placement.position[0] = `(${clone.placement.position[0]}) + 15`;
      commit(`Duplicate ${source.name}`, (draft) => {
        draft.nodes.push(clone);
      }, [clone.id]);
    },

    toggleSelectedEnabled() {
      const selected = new Set(get().selectedNodeIds);
      if (selected.size === 0) return;
      commit("Toggle feature suppression", (draft) => {
        for (const node of draft.nodes) if (selected.has(node.id)) node.enabled = !node.enabled;
      });
    },

    toggleNodeVisibility(nodeId) {
      commit("Toggle visibility", (draft) => {
        const node = draft.nodes.find((candidate) => candidate.id === nodeId);
        if (node) node.visible = !node.visible;
      });
    },

    undo() {
      const state = get();
      const entry = state.historyPast.at(-1);
      if (!entry) return;
      set({
        document: applyPatches(state.document, entry.inversePatches),
        revision: state.revision + 1,
        historyPast: state.historyPast.slice(0, -1),
        historyFuture: [entry, ...state.historyFuture],
        saveStatus: "unsaved",
        statusMessage: `Undo ${entry.label}`
      });
    },

    redo() {
      const state = get();
      const entry = state.historyFuture[0];
      if (!entry) return;
      set({
        document: applyPatches(state.document, entry.patches),
        revision: state.revision + 1,
        historyPast: [...state.historyPast, entry],
        historyFuture: state.historyFuture.slice(1),
        saveStatus: "unsaved",
        statusMessage: `Redo ${entry.label}`
      });
    },

    canUndo() {
      return get().historyPast.length > 0;
    },

    canRedo() {
      return get().historyFuture.length > 0;
    },

    setEvaluation(evaluation) {
      if (evaluation.revision !== get().revision) return;
      set({
        evaluation,
        diagnostics: evaluation.diagnostics,
        kernelStatus: evaluation.diagnostics.some((item) => item.severity === "error") ? "error" : "ready",
        statusMessage: evaluation.diagnostics.some((item) => item.severity === "error")
          ? `${evaluation.diagnostics.length} modeling issue${evaluation.diagnostics.length === 1 ? "" : "s"}`
          : `Rebuilt in ${Math.round(evaluation.durationMs)} ms`
      });
    },

    setKernelStatus(status, message) {
      set({ kernelStatus: status, statusMessage: message ?? get().statusMessage });
    },

    setSaveStatus(status, message) {
      set({ saveStatus: status, statusMessage: message ?? get().statusMessage });
    }
  };
});
